import React, { useState } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'

// Components
import ParseContent from 'components/shared/ParseContent'

// Elements
import TitleDefault from 'components/elements/TitleDefault'
import ButtonDefault from 'components/elements/ButtonDefault'

import { ScrollRevealItem, ScrollRevealStagger } from 'components/shared/ScrollReveal'

const Wrapper = styled.section``

const InnerWrapper = styled.div`
  background-color: ${(props) => props.theme.color.face.custom};
`

const ProcessWrapper = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.color.face.light};
`

const SubTitleWrapper = styled(motion.div)`
  &:hover {
    cursor: pointer;
  }
`

const SubTitle = styled.h3`
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['24']};
  color: ${(props) => props.theme.color.text.contrast};

  @media (max-width: 991px) {
    font-size: ${(props) => props.theme.font.size.base};
  }
`

const AfterSubTitle = styled.div`
  font-size: ${(props) => props.theme.font.size['21']};
  font-weight: ${(props) => props.theme.font.weight.l};
  color: ${(props) => props.theme.color.text.contrast};
`

const Content = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    font-size: ${(props) => props.theme.font.size.base};
    color: ${(props) => props.theme.color.text.dark};
  }
`

interface PostProcessProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Posts
}

function PostProcess({ fields }: PostProcessProps) {
  const [currentIndex, setCurrentIndex] = useState<number | null>(0)
  const ctaLink = fields?.links?.[0]?.link

  return (
    <Wrapper className="container my-5 py-5" id={fields?.anchorId || ''}>
      {fields?.title && <TitleDefault>{fields?.title}</TitleDefault>}

      <div className="row justify-content-center mt-4">
        <InnerWrapper className="col-lg-9 pb-5">
          <ScrollRevealStagger>
            {fields?.step?.map((step, index: number) => {
              const isOpen = index === currentIndex

              return (
                <ScrollRevealItem key={step?.subtitle || String(index)}>
                  <ProcessWrapper className="mx-lg-5 mx-2 pt-5 pb-3">
                    <SubTitleWrapper
                      onClick={() => {
                        if (index === currentIndex) {
                          setCurrentIndex(null)
                        } else {
                          setCurrentIndex(index)
                        }
                      }}
                    >
                      <div className="d-flex w-100 align-items-center">
                        <SubTitle className="w-75 mb-0">{step?.subtitle}</SubTitle>
                        <AfterSubTitle className="d-flex justify-content-end w-25 mr-md-3">
                          {isOpen ? '-' : '+'}
                        </AfterSubTitle>
                      </div>
                    </SubTitleWrapper>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          initial="collapsed"
                          animate="open"
                          exit="collapsed"
                          variants={{
                            open: {
                              opacity: 1,
                              height: 'auto',
                              overflow: 'hidden',
                            },
                            collapsed: {
                              opacity: 0,
                              height: 0,
                              overflow: 'hidden',
                            },
                          }}
                          transition={{
                            duration: 0.8,
                            ease: [0.04, 0.62, 0.23, 0.98],
                          }}
                        >
                          <Content content={step?.description} className="py-4" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </ProcessWrapper>
                </ScrollRevealItem>
              )
            })}
          </ScrollRevealStagger>
          {ctaLink && (
            <div className="d-flex justify-content-center mt-5">
              <ButtonDefault to={ctaLink.url || ''} className="text-center">
                {ctaLink.title}
              </ButtonDefault>
            </div>
          )}
        </InnerWrapper>
      </div>
    </Wrapper>
  )
}

export default PostProcess
